import { useEffect, useMemo, useState } from "react";
import { apiClient, ApiError } from "../api/client.js";
import type { AssetVersionsResponse, InboxStatus } from "../api/types.js";
import { formatBytes } from "./asset-helpers.js";
import { RestoreTargetPicker, suggestRestoreTarget } from "./restore-target-picker.js";

// 3.B.2.1: Asset-detail-modal:n Historia-välilehti.
//
// Hakee daemon-inbox:n kautta snapshot-list:n asset.currentPath:lle ja
// näyttää listan: per snapshot aikaleima + koko + tiedostomäärä + restore-CTA.
// Restore-flow:
//   1. Käyttäjä klikkaa "Palauta tästä snapshotista" → laajenee restore-form
//   2. RestoreTargetPicker määrittää kohdepolun (~/Kratti-restored/<ts>/)
//   3. Mode-radio: vain tiedostot / oikeuksilla / kaikki meta
//   4. POST inbox snapshot-restore → poll status
//   5. Onnistuessa "Avaa Finderissa" -nappi (reveal-in-finder)
//
// Vaatii machineId-prop:n — asset-detail-modal valitsee aktiivisen daemonin
// (yksi machine v1:ssä).

export type Snapshot = {
  id: string;
  sourcePath: string;
  startTime: string;
  endTime: string;
  fileCount: number;
  totalSize: number;
  description?: string;
};

export type AssetHistoryTabProps = {
  assetId: string;
  assetPath: string;
  /** Tiedoston indeksointi-aika (assets.createdAt). Käytetään ⊘-merkkiin: snapshot ennen tätä = asset ei vielä ollut. */
  assetCreatedAt?: string;
  machineId: string | null;
};

/** Versio-indikaattorin tila per snapshot. */
type VersionState =
  | { kind: "identical" } // ✓ sama versio
  | { kind: "different"; sizeOld: number | null; sizeNew: number | null } // ✏ eri versio + koon erot
  | { kind: "before-asset" } // ⊘ asset ei vielä ollut
  | { kind: "unknown" }; // ? versiotieto puuttuu

const POLL_INTERVAL_MS = 1000;
const SNAPSHOT_LIST_TIMEOUT_MS = 60_000;
const RESTORE_TIMEOUT_MS = 60 * 60_000;

export function AssetHistoryTab({
  assetId,
  assetPath,
  assetCreatedAt,
  machineId,
}: AssetHistoryTabProps) {
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const [versions, setVersions] = useState<AssetVersionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSnapshot, setActiveSnapshot] = useState<string | null>(null);

  // 3.B.2.3: hae GET /assets/:id/versions paralleelisti snapshot-listin kanssa.
  // Käytetään ✓/✏/⊘-merkkien laskemiseen.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .getAssetVersions(assetId)
      .then((v) => {
        if (!cancelled) setVersions(v);
      })
      .catch(() => {
        if (!cancelled) setVersions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  // Hae snapshot-lista kun komponentti mountataan tai machineId muuttuu
  useEffect(() => {
    if (!machineId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSnapshots(null);
    (async () => {
      try {
        const created = await apiClient.postInboxRequest(machineId, {
          type: "snapshot-list",
          request: { sourcePath: assetPath },
        });
        const result = await pollInboxUntilDone(
          created.id,
          SNAPSHOT_LIST_TIMEOUT_MS
        );
        if (cancelled) return;
        if (result.status === "completed") {
          const raw = (result.result as { snapshots?: Snapshot[] } | null)?.snapshots ?? [];
          setSnapshots(raw);
        } else {
          setError(result.error ?? `Snapshot-haku ${result.status}`);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machineId, assetPath]);

  if (!machineId) {
    return (
      <p className="text-sm text-slate-500">
        Daemon-instanssi ei ole tiedossa — Historia-näkymä ei ole käytettävissä.
        Käy /levyt-sivulla varmistamassa että Macin daemon näkyy.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {loading && (
        <p className="text-sm text-slate-500">Haetaan snapshot-listaa Macilta…</p>
      )}
      {error && (
        <p className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}
      {snapshots && snapshots.length === 0 && !loading && (
        <p className="text-sm text-slate-500">
          Ei snapshotteja jotka kattaisivat tämän polun. Daemon on ehkä ehtinyt
          ottaa snapshot:in vasta tämän tiedoston jälkeen.
        </p>
      )}
      {snapshots && snapshots.length > 0 && (
        <ul className="divide-y divide-slate-800">
          {snapshots.map((s) => {
            const state = computeVersionState(s, versions, assetCreatedAt);
            return (
              <li key={s.id} className="py-3">
                <SnapshotRow
                  snapshot={s}
                  versionState={state}
                  assetPath={assetPath}
                  machineId={machineId}
                  expanded={activeSnapshot === s.id}
                  onToggle={() => setActiveSnapshot((cur) => (cur === s.id ? null : s.id))}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SnapshotRow({
  snapshot,
  versionState,
  assetPath,
  machineId,
  expanded,
  onToggle,
}: {
  snapshot: Snapshot;
  versionState: VersionState;
  assetPath: string;
  machineId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const disabled = versionState.kind === "before-asset";
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <VersionBadge state={versionState} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-200">
              {formatTimestamp(snapshot.startTime)}
            </p>
            <p className="text-xs text-slate-500">
              {snapshot.fileCount.toLocaleString("fi-FI")} tiedostoa ·{" "}
              {formatBytes(snapshot.totalSize)}
            </p>
            <VersionDetail state={versionState} />
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className="rounded bg-cyan-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
          title={
            disabled
              ? "Tiedostoa ei ollut tässä snapshot:issa"
              : "Avaa restore-lomake"
          }
        >
          {expanded ? "Sulje" : "Palauta tästä snapshotista"}
        </button>
      </div>
      {expanded && (
        <RestoreForm snapshot={snapshot} assetPath={assetPath} machineId={machineId} />
      )}
    </div>
  );
}

function VersionBadge({ state }: { state: VersionState }) {
  switch (state.kind) {
    case "identical":
      return (
        <span
          title="Sama versio kuin nyt — restore ei muuta mitään"
          className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-900/60 text-emerald-200"
        >
          ✓
        </span>
      );
    case "different":
      return (
        <span
          title="Eri versio kuin nyt — restore palauttaa eri sisällön"
          className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-yellow-900/60 text-yellow-200"
        >
          ✏
        </span>
      );
    case "before-asset":
      return (
        <span
          title="Tiedostoa ei vielä ollut tässä snapshot:issa"
          className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-800 text-slate-500"
        >
          ⊘
        </span>
      );
    case "unknown":
      return (
        <span
          title="Versiotieto ei saatavilla (legacy-snapshot)"
          className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-slate-600"
        >
          ?
        </span>
      );
  }
}

function VersionDetail({ state }: { state: VersionState }) {
  if (state.kind !== "different") return null;
  const oldFmt = state.sizeOld !== null ? formatBytes(state.sizeOld) : "?";
  const newFmt = state.sizeNew !== null ? formatBytes(state.sizeNew) : "?";
  return (
    <p className="mt-0.5 text-xs text-yellow-300">
      {newFmt} → {oldFmt} (snapshotissa)
    </p>
  );
}

function computeVersionState(
  snapshot: Snapshot,
  versions: AssetVersionsResponse | null,
  assetCreatedAt: string | undefined
): VersionState {
  // 1. Asset luotu vasta snapshot:in jälkeen → ei voinut olla snapshot:issa
  if (assetCreatedAt) {
    const created = Date.parse(assetCreatedAt);
    const snap = Date.parse(snapshot.startTime);
    if (Number.isFinite(created) && Number.isFinite(snap) && snap < created) {
      return { kind: "before-asset" };
    }
  }

  // 2. Tarkista löytyykö versio /assets/:id/versions-vastauksesta
  const versionRow = versions?.versions.find((v) => v.snapshotId === snapshot.id);
  if (versionRow) {
    if (versionRow.isIdentical) return { kind: "identical" };
    return {
      kind: "different",
      sizeOld: coerceNumber(versionRow.sizeBytes),
      sizeNew: coerceNumber(versions!.current.sizeBytes),
    };
  }

  // 3. Snapshot listoissa mutta versio-projektiota ei ole — tuntematon
  return { kind: "unknown" };
}

function coerceNumber(v: unknown): number | null {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function RestoreForm({
  snapshot,
  assetPath,
  machineId,
}: {
  snapshot: Snapshot;
  assetPath: string;
  machineId: string;
}) {
  const initialTarget = useMemo(() => suggestRestoreTarget(), []);
  const [target, setTarget] = useState(initialTarget);
  const [mode, setMode] = useState<"files-only" | "with-permissions" | "with-metadata">(
    "with-permissions"
  );
  const [status, setStatus] = useState<
    "idle" | "submitting" | "running" | "completed" | "failed" | "cancelled"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const [restoredPath, setRestoredPath] = useState<string | null>(null);
  const [revealStatus, setRevealStatus] = useState<"idle" | "opening" | "error">("idle");

  async function startRestore() {
    setStatus("submitting");
    setError(null);
    try {
      const created = await apiClient.postInboxRequest(machineId, {
        type: "snapshot-restore",
        request: {
          snapshotId: snapshot.id,
          sourcePath: assetPath,
          targetPath: target,
          mode,
        },
      });
      setStatus("running");
      const result = await pollInboxUntilDone(created.id, RESTORE_TIMEOUT_MS);
      if (result.status === "completed") {
        const r = result.result as { restoredPath?: string } | null;
        setRestoredPath(r?.restoredPath ?? target);
        setStatus("completed");
      } else if (result.status === "cancelled") {
        setStatus("cancelled");
      } else {
        setStatus("failed");
        setError(result.error ?? `Restore ${result.status}`);
      }
    } catch (err) {
      setStatus("failed");
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    }
  }

  async function revealInFinder() {
    if (!restoredPath) return;
    setRevealStatus("opening");
    try {
      const created = await apiClient.postInboxRequest(machineId, {
        type: "reveal-in-finder",
        request: { path: restoredPath },
      });
      const result = await pollInboxUntilDone(created.id, SNAPSHOT_LIST_TIMEOUT_MS);
      if (result.status === "completed") {
        setRevealStatus("idle");
      } else {
        setRevealStatus("error");
      }
    } catch {
      setRevealStatus("error");
    }
  }

  const busy = status === "submitting" || status === "running";

  return (
    <div className="space-y-3 rounded-md border border-slate-800 bg-slate-900/40 px-3 py-3">
      <RestoreTargetPicker
        value={target}
        onChange={setTarget}
        disabled={busy || status === "completed"}
        machineId={machineId}
      />
      <fieldset className="space-y-1">
        <legend className="block text-xs font-medium uppercase tracking-wide text-slate-400">
          Mitä palautetaan
        </legend>
        <div className="space-y-1 text-sm text-slate-300">
          {[
            { v: "files-only" as const, l: "Vain tiedostot (ei oikeuksia, ei aikaleimoja)" },
            { v: "with-permissions" as const, l: "Tiedostot + permissionit + aikaleimat" },
            { v: "with-metadata" as const, l: "Kaikki metadata (sis. owner-tiedot)" },
          ].map((opt) => (
            <label key={opt.v} className="flex items-center gap-2">
              <input
                type="radio"
                name={`mode-${snapshot.id}`}
                value={opt.v}
                checked={mode === opt.v}
                onChange={() => setMode(opt.v)}
                disabled={busy || status === "completed"}
              />
              <span>{opt.l}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {status === "idle" && (
        <button
          type="button"
          onClick={() => void startRestore()}
          disabled={target.trim().length === 0}
          className="rounded bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
        >
          Palauta nyt
        </button>
      )}
      {(status === "submitting" || status === "running") && (
        <p className="text-sm text-slate-400">
          {status === "submitting"
            ? "Lähetetään pyyntö daemonille…"
            : "Kopia palauttaa tiedostoja. Iso restore voi kestää useita minuutteja."}
        </p>
      )}
      {status === "completed" && (
        <div className="space-y-2 rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm">
          <p className="text-emerald-200">Palautettu polkuun: {restoredPath ?? target}</p>
          <button
            type="button"
            onClick={() => void revealInFinder()}
            disabled={revealStatus === "opening"}
            className="rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            {revealStatus === "opening" ? "Avataan…" : "Avaa Finderissa"}
          </button>
          {revealStatus === "error" && (
            <p className="text-xs text-yellow-300">Finder-avaus epäonnistui — avaa polku manuaalisesti.</p>
          )}
        </div>
      )}
      {status === "cancelled" && (
        <p className="text-sm text-yellow-300">Restore peruutettu.</p>
      )}
      {status === "failed" && error && (
        <p className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}
    </div>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("fi-FI", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Pollaa inbox-statusta `intervalMs` välein kunnes status muuttuu
 * pending/in_progress -tilasta toiseksi tai timeout täyttyy.
 * Eksportoitu jotta 3.B.3.x-komponentit voivat käyttää samaa logiikkaa.
 */
export async function pollInboxUntilDone(
  requestId: string,
  timeoutMs: number,
  intervalMs: number = POLL_INTERVAL_MS
): Promise<InboxStatus> {
  const startedAt = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() - startedAt > timeoutMs) {
      return {
        id: requestId,
        status: "timeout",
        result: null,
        error: "Daemon ei vastannut ajoissa",
        createdAt: new Date(startedAt).toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }
    const s = await apiClient.getInboxStatus(requestId);
    if (s.status !== "pending" && s.status !== "in_progress") return s;
    await new Promise<void>((resolve) => setTimeout(resolve, intervalMs));
  }
}

